import InvalidDateException from '~/errors/InvalidDateException';

import { type UUID } from '~/types/common';

import { selectableReportFields } from '~/modules/dashboard/components/DashboardReport/constants';

import { toCamelCase } from '@vestigas/utils';
import { isCustomFieldName } from '~/utils/customField';
import { log } from '~/utils/logging';

type CustomFieldData = {
  displayName: string;
  id: UUID;
  key: string;
  level: 'global' | 'item';
  name: string;
};

type ReportFieldConfig = {
  customFieldId?: string;
  name: string;
};

const getCustomFieldData = (customField, customFields = []) => {
  const customFieldData = customFields.find(({ key }) => key === customField);

  return customFieldData;
};

const getCustomFieldConfig = (customField, customFields = []) => {
  const customFieldData = getCustomFieldData(customField, customFields);

  if (!customFieldData) {
    return null;
  }

  return {
    customFieldId: customFieldData.id,
    name:
      customFieldData.level === 'global'
        ? 'custom_field_global_level'
        : 'custom_field_item_level',
  };
};

const getFieldConfig = (field) => {
  if (!selectableReportFields.has(field)) {
    log.error({
      message: `Invalid field name in report config: ${field}`,
      error: new InvalidDateException(
        `Invalid field name in report config: ${field}`,
      ),
    });

    return null;
  }

  return {
    name: field,
  };
};

export const getReportConfiguration = (
  fields,
  customFields: CustomFieldData[],
): ReportFieldConfig[] => {
  const fieldsConfiguration = fields
    .map((field) => {
      if (!isCustomFieldName(field)) {
        return getFieldConfig(field);
      }

      return getCustomFieldConfig(field, customFields);
    })
    .filter(Boolean);

  return fieldsConfiguration;
};

export const getReportOrder = (
  fields: string[],
  customFields: CustomFieldData[],
) => {
  const reportOrder = fields
    .map((field) => {
      if (!isCustomFieldName(field)) {
        if (!selectableReportFields.has(field)) {
          log.error({
            message: `Invalid field name in report order: ${field}`,
            error: new InvalidDateException(
              `Invalid field name in report order: ${field}`,
            ),
          });

          return null;
        }

        return field;
      }

      const customFieldData = getCustomFieldData(field, customFields);

      return customFieldData?.id;
    })
    .filter(Boolean);

  // Make sure amount and unit type are always included and at the end.
  const reportOrderWithAmountAndUnit = [
    ...reportOrder.filter(
      (field) => !['amount', 'unitType'].includes(toCamelCase(field)),
    ),
    'amount',
    'unitType',
  ];

  return reportOrderWithAmountAndUnit;
};
